import { Injectable } from '@angular/core';
import { SimulationStep } from '../generated/models_pb';
import { CanvasService } from './canvas.service';
import * as THREE from 'three';
import { DelayService } from './delay.service';
import { RenderState } from '../enums/simulation-state';
import { SimulationRender } from '../interfaces/simulation-render';
import { SimulationRunDetails } from '../interfaces/simulation-run-details';
import { CellColoring } from '../interfaces/simulation-results-cell-coloring';

@Injectable({
  providedIn: 'root',
})
export class SimulationRendererService {
  constructor(
    private canvasService: CanvasService,
    private delayService: DelayService
  ) {}

  public runDetails?: SimulationRunDetails;
  public onCompleted?: () => void;
  public onFailed?: (error: Error) => void;
  private paused: boolean = false;
  private pausedSteps?: {
    minStep: number;
    maxStep: number;
    renderObject: SimulationRender;
  };
  private instantRenderRequested: boolean = false;
  private rendering = false;
  

  private async paintStepsInGroup(
    minStep: number,
    maxStep: number,
    renderObject: SimulationRender
  ) {
    for (let i: number = minStep; i <= maxStep; i++) {
      if (!this.instantRenderRequested) {
        await this.delayService.delay(50);
      }
      if (this.paused) {
        this.storePausedSteps(i, maxStep, renderObject);
        return;
      }
      this.paintStepGroup(renderObject.steps, i);
      renderObject.state = RenderState.Rendered;
    }
  }

  private async renderSteps(renderObject: SimulationRender) {
    if (renderObject.state !== RenderState.None) {
      return;
    }
    if (renderObject.steps.length === 0) {
      renderObject.state = RenderState.Rendered;
      return;
    }
    renderObject.state = RenderState.Rendering;
    const minStep: number = this.getMinTimestepGroup(renderObject.steps);
    const maxStep: number = this.getMaxTimestepGroup(renderObject.steps);
    if (this.runDetails) {
      this.runDetails.complexityRating = renderObject.complexityRate;
      this.runDetails.agentCount = renderObject.startingAgents;
      this.runDetails.currentStep = renderObject.index;
      this.runDetails.cellCount = renderObject.totalCells.length;
    }
    await this.paintStepsInGroup(minStep, maxStep, renderObject);
  }

  public async render(stepsToRender: SimulationRender[]) {
    if (this.rendering) {
      return;
    }
    this.rendering = true;
      console.log("value for this.paused "+this.paused)
    for (const step of stepsToRender) {
      if (this.paused) {
        return;
      }
      await this.renderSteps(step);
    }

    if (this.onCompleted) {
      this.onCompleted();
    }
    this.rendering = false;
  }

  public async renderNextStep(stepsToRender: SimulationRender[]) {
    if (this.pausedSteps) {
      await this.renderPausedSteps();
      return;
    }
    for(const step of stepsToRender) {
      if (step.state !== RenderState.None) {
        continue;
      }
      this.paused = false;
      await this.renderSteps(step);
      this.paused = true;
      break;
    }

    if (this.onCompleted) {
      this.onCompleted();
    }
  }

  public async renderInstantly(stepsToRender: SimulationRender[]) {
    console.log('In render instantly '+ this.instantRenderRequested);
    this.instantRenderRequested = true;
    console.log('In render instantly '+ this.instantRenderRequested);
    this.paused = false;
    await this.renderPausedSteps();
    this.rendering = false;
    await this.render(stepsToRender);
    this.animate();
    this.instantRenderRequested = false;
    if (this.onCompleted) {
      this.onCompleted();
    }
  }

  public pause() {
    this.paused = true;
  }

  private async renderPausedSteps() {
    console.log('In render paused state');
    if (!this.pausedSteps) {
       console.log('In not pausedState');
      return;
    }
    await this.paintStepsInGroup(
      this.pausedSteps?.minStep,
      this.pausedSteps?.maxStep,
      this.pausedSteps?.renderObject
    );
    this.pausedSteps = undefined;
  }

  public async resume(stepsToRender: SimulationRender[]) {
    this.paused = false;
    await this.renderPausedSteps();
    this.rendering = false;
    await this.render(stepsToRender);
  }

  public reset() {
    this.paused = false;
    this.pausedSteps = undefined;
    this.rendering = false;
    this.canvasService.eraseAllAgentsFromCanvas();
  }

  private getMinTimestepGroup(stepGroup: SimulationStep.AsObject[]) {
    const minStep: number = Math.min(
      ...stepGroup.map(function (o) {
        return o.timesteps;
      })
    );
    return minStep;
  }

  private getMaxTimestepGroup(stepGroup: SimulationStep.AsObject[]) {
    const arr: number[] = stepGroup.map(function (o) {
      return o.timesteps;
    });

    let len: number = arr.length;
    let max: number = -Infinity;

    while (len--) {
      max = arr[len] > max ? arr[len] : max;
    }
    return max;
  }

  public paintCellColorings(
    cellColorings: CellColoring[],
  ) {
    cellColorings.forEach((cell) => {
      this.canvasService.paintCanvas(
        cell.index,
        new THREE.Color(cell.color.r, cell.color.g, cell.color.b)
      );
    });

    this.animate();
  }

  private async paintStepGroup(
    steps: Array<SimulationStep.AsObject>,
    group: number

  ) {
    let animate: boolean = false;
    steps.forEach((step) => {
      if (step.timesteps == group) {
        if (step.color) {
          this.canvasService.paintCanvas(
            step.index,
            new THREE.Color(step.color.r, step.color.g, step.color.b)
          );
          animate = true;
        }
      }
    });
    
    if (animate && !this.instantRenderRequested){
        this.animate();
     }
    
  }

  private animate() {
    this.canvasService.updateTexture();
    this.canvasService.animate();
  }

  private storePausedSteps(
    minStep: number,
    maxStep: number,
    renderObject: SimulationRender
  ) {
    this.pausedSteps = {
      minStep: minStep,
      maxStep: maxStep,
      renderObject: renderObject,
    };
  }
}
